import PropTypes from 'prop-types';
import { createContext, useCallback, useMemo, useState, useEffect } from 'react';
import { Trans, withTranslation } from 'react-i18next';
import _ from 'lodash';

import { getRegion } from '../../../api/managers/store';
import { getPlanInfo } from '../../../api/manage';
import { getAllCameras } from '../../../api/cameras';
import { getOrganisationsLoggedInUserHasAccessTo } from '../../../api/user';

import translationKeys from '../../../translations/keys';
import routes, { canUserAccessRoute } from '../../../pages/routes';

import useDemo from '../../../hooks/useDemo';
import useWhiteLabelComponent from '../../../hooks/useWhiteLabelComponent';
import { useWhiteLabelInfo } from '../../white-label';

import host, { isMobileApp } from '../../../utils/getHost';
import {
    SMART_MOTION_ACCESS_CHANGED_EVENT,
    computeSmartMotionAccess,
    readCachedSmartMotionAccess
} from '../../../utils/smartMotionAlertsAccess';
import { LOCAL_STORAGE_PLAN_CANCELLED_BANNER_DISMISSED } from '../../../utils/manageStorage';
import { VL_BLACK, WHITE } from '../../../utils/colours';
import { isSitesecWhiteLabel } from '../../../utils/whiteLabel';
import { getDevelopmentSiteName } from '../../../utils/developmentSite';

// Icons
import { ReactComponent as LogOutIcon } from '../../../assets/images/icons/log-out.svg';

import ErrorBoundary from '../../error-boundary';
import ErrorScreen from '../../error-screen';
import WhiteLabel from '../../white-label';
import Tabs from '../../tabs';
import Icon from '../../icon';
import P from '../../p';
import Link from '../../link';
import MobileAppHeader from '../../mobile-app-header';
import Flex from '../../layout/flex';
import IconButton from '../../button/icon';
import { AppLayoutContainer, Banner, BannerContainer, DevelopmentSiteBanner, PlanCancelledBanner, PageContent, SidebarContainer } from './AppLayout.styles';


// When showing a banner, set this to the timestamp of when banner was released
// When not showing a banner, this must be null
const BANNER_UPDATED = null;
// Restrict banner to only show for users in certain regions, e.g. ['euwest1']
// Set this to null if no restriction applies
const BANNER_REGIONS = null;
// Restrict banner to only show on certain sites
// Function receives object of white label info and must return a boolean
// Set this to null if no restriction applies
const BANNER_WHITE_LABEL_CONDITION = ({ isVideoloft, isTakeawayVendor, appName }) => isVideoloft && !isTakeawayVendor;

// Mobile nav menu shown for screen widths <= 800 px
const mobileBreakpoint = 800;

// Context used to let pages control mobile app header
export const MobileAppHeaderContext = createContext();
MobileAppHeaderContext.displayName = 'MobileAppHeaderContext';

/** Manages layout for pages that should have the 'App layout' view. This includes a sidebar on the left of the screen. */
const AppLayout = ({
    routeConfig,
    logout,
    children,
    accountInfo,
    t,
    ...props
}) => {
    
    const { accentColour, accentColour2, whiteOverAccent, useAccentColour2, gradients, backgroundGradient, isVideoloft, navColour, navTextColour, ...whiteLabelInfo } = useWhiteLabelComponent();
    
    const { isDemo } = useDemo();

    const isVideoloftDemo = isDemo && isVideoloft;
    const isSitesec = isSitesecWhiteLabel(whiteLabelInfo, host);
    const developmentSiteName = getDevelopmentSiteName();

    // Dismissal deliberately only lasts for this layout instance so the notice reappears after a reload or a new session
    const [showDevelopmentSiteBanner, setShowDevelopmentSiteBanner] = useState(
        () => developmentSiteName !== null
    );

    // Whether we are showing banner
    const [showBanner, setShowBanner] = useState(
        // We have a specific banner for VL demo but do not show it in mboile app
        (isVideoloftDemo && !isMobileApp) ||
        // Otherwise normal banner logic
        (
            // Do we want to show a banner?
            !!BANNER_UPDATED &&
            // Do not show on mobile app
            !isMobileApp &&
            // Do not show on demo
            !isDemo &&
            // Check user region if required
            (!BANNER_REGIONS || BANNER_REGIONS.includes(getRegion())) &&
            // Check whether site is Videoloft if required
            (!BANNER_WHITE_LABEL_CONDITION || BANNER_WHITE_LABEL_CONDITION({...whiteLabelInfo, isVideoloft})) &&
            // We store when user last closed the banner in local storage so we can compare it to BANNER_UPDATED to see
            // whether they previously closed this version of the banner
            (!localStorage.bannerClosed || parseInt(localStorage.bannerClosed) < BANNER_UPDATED)
        )
    );

    // Plan cancellation state - stores info about user's own plan and accessed accounts
    const [planCancelledInfo, setPlanCancelledInfo] = useState({
        isOwnPlanCancelled: false,
        canReactivate: false, // true if isSelfReg or isEUP
        suspendedAccounts: [] // array of { email } for accounts user has access to that are suspended
    });

    // Whether the plan cancelled banner has been dismissed by the user (persists across sessions, per account)
    const [planCancelledBannerDismissed, setPlanCancelledBannerDismissed] = useState(() => {
        // Check if the stored uid matches the current user's uid
        const dismissedForUid = localStorage[LOCAL_STORAGE_PLAN_CANCELLED_BANNER_DISMISSED];
        return dismissedForUid === String(accountInfo?.uid);
    });

    useEffect(() => {
        // Only fetch plan info if not in demo mode and not on mobile app
        if (!isDemo && !isMobileApp) {
            // Fetch both plan info and organisations in parallel
            Promise.all([
                getPlanInfo().catch(() => null),
                getOrganisationsLoggedInUserHasAccessTo().catch(() => [])
            ]).then(([planInfo, organisations]) => {
                // Check user's own plan - use suspended flag (not isOnFreePlan which includes non-suspended free plans)
                // Use !! to convert truthy values (e.g. 1) to boolean
                const isOwnPlanCancelled = !!planInfo?.suspended;
                const canReactivate = planInfo?.isSelfReg === true || planInfo?.isEUP === true;

                // Check for suspended accounts the user has access to
                const suspendedAccounts = organisations
                    .filter(org => org.suspended)
                    .map(org => ({ email: org.email }));

                setPlanCancelledInfo({
                    isOwnPlanCancelled,
                    canReactivate,
                    suspendedAccounts
                });
            });
        }
    }, [isDemo]);

    // Determine if we should show the plan cancelled banner
    const showPlanCancelledBanner = !isSitesec && !planCancelledBannerDismissed && (planCancelledInfo.isOwnPlanCancelled || planCancelledInfo.suspendedAccounts.length > 0);
    const showTopBannerArea = showDevelopmentSiteBanner || showBanner;

    // State for mobile app header
    // setState function exposed through Context API and controlled by `useMobileAppHeader`
    const [mobileAppHeaderState, setMobileAppHeaderState] = useState({
        text: null,
        backButtonOnClick: null,
        actionButtons: null,
        custom: false
    });
    const mobileAppHeaderContextValue = useMemo(() => {
        return {
            setMobileAppHeaderState
        };
    }, []);

    return (
        <AppLayoutContainer
            $mobileBreakpoint={mobileBreakpoint}
            $banner={showTopBannerArea}
            $planCancelledBanner={showPlanCancelledBanner}
            {...props}
        >
            {/* Mobile app header */}
            {
                isMobileApp && (
                    <MobileAppHeader
                        layout={mobileAppHeaderState.custom ? 'custom' : undefined}
                    >
                        {
                            // Render nothing for custom contents as page will control it
                            !mobileAppHeaderState.custom && (
                                <>
                                    <MobileAppHeader.BackButton
                                        disabled={!mobileAppHeaderState.backButtonOnClick}
                                        onClick={mobileAppHeaderState.backButtonOnClick}
                                    />

                                    {
                                        mobileAppHeaderState.text && (
                                            <MobileAppHeader.Title>
                                                {mobileAppHeaderState.text}
                                            </MobileAppHeader.Title>
                                        )
                                    }

                                    {
                                        mobileAppHeaderState.actionButtons?.length > 0 && (
                                            <Flex
                                                gap={2}
                                            >
                                                {
                                                    mobileAppHeaderState.actionButtons.map(({ Icon, ...props }) => {
                                                        return (
                                                            <IconButton
                                                                svgMaxWidth="60%"
                                                                svgMaxHeight="60%"
                                                                {...props}
                                                                style={{color: 'inherit'}}
                                                            >
                                                                <Icon />
                                                            </IconButton>
                                                        );
                                                    })
                                                }
                                            </Flex>
                                        )
                                    }
                                </>
                            )
                        }
                    </MobileAppHeader>
                )
            }

            {/* Sidebar. Shown as header with popup menu on mobile. */}
            <Sidebar
                mobileBreakpoint={mobileBreakpoint}
                logout={logout}
                accountInfo={accountInfo}
            />

            {
                showTopBannerArea && (
                    <BannerContainer>
                        {
                            showDevelopmentSiteBanner && (
                                <DevelopmentSiteBanner role="status">
                                    <P>
                                        {t(translationKeys.banner.DEVELOPMENT_SITE, { siteName: developmentSiteName })}
                                    </P>
                                    <Icon
                                        variant="close"
                                        accessibilityDescription="Dismiss development site banner"
                                        height={16}
                                        width={16}
                                        onClick={() => setShowDevelopmentSiteBanner(false)}
                                    />
                                </DevelopmentSiteBanner>
                            )
                        }

                        {
                            showBanner && (
                                <Banner
                                    style={{
                                        background: isVideoloft ? accentColour : accentColour2 ?? accentColour,
                                        color: whiteOverAccent ? WHITE() : VL_BLACK()
                                    }}
                                >
                                    <P>
                                        {
                                            // There is a specific banner for VL demo
                                            isVideoloftDemo ? (
                                                <Trans i18nKey={translationKeys.banner.VL_DEMO_BANNER_MESSAGE}>
                                                    Arrange a free Videoloft Proof of Concept. <Link to="https://videoloft.com/request-proof-of-concept/" colour={whiteOverAccent ? WHITE() : VL_BLACK()}>Get Started</Link>
                                                </Trans>
                                            ) : (
                                                // t(translationKeys.banner.BANNER_MESSAGE)
                                                <Trans i18nKey={translationKeys.banner.BANNER_MESSAGE} components={[<strong />]}>
                                                    Scheduled maintenance: Wed, Mar 5, 5 AM EST – No downtime expected. Contact us if you experience any issues.
                                                </Trans>
                                            )
                                        }
                                    </P>
                                    <Icon
                                        variant="close"
                                        accessibilityDescription="Close"
                                        height={16}
                                        width={16}
                                        onClick={() => {
                                            setShowBanner(false);
                                            localStorage.bannerClosed = new Date().getTime();
                                        }}
                                    />
                                </Banner>
                            )
                        }
                    </BannerContainer>
                )
            }

            {
                showPlanCancelledBanner && (() => {
                    // Calculate total number of banners that would be shown
                    const totalBanners = (planCancelledInfo.isOwnPlanCancelled ? 1 : 0) + planCancelledInfo.suspendedAccounts.length;
                    // If more than 3, show a single message for external accounts instead of individual ones
                    const showMultipleAccountsMessage = totalBanners > 3 && planCancelledInfo.suspendedAccounts.length > 0;

                    return (
                        <PlanCancelledBanner>
                            <P>
                                {/* User's own plan cancelled message */}
                                {planCancelledInfo.isOwnPlanCancelled && (
                                    <>
                                        {planCancelledInfo.canReactivate ? (
                                            <Trans i18nKey={translationKeys.banner.PLAN_CANCELLED_REACTIVATE}>
                                                Plan cancelled - live feeds, recording and uploads are suspended. <Link to="/account/plan-billing" colour="white">Reactivate your plan.</Link>
                                            </Trans>
                                        ) : (
                                            t(translationKeys.banner.PLAN_CANCELLED_CONTACT_PROVIDER)
                                        )}
                                    </>
                                )}
                                {/* Suspended accounts messages - show single message if too many */}
                                {showMultipleAccountsMessage ? (
                                    <span>
                                        {planCancelledInfo.isOwnPlanCancelled && <br />}
                                        {t(translationKeys.banner.PLAN_CANCELLED_MULTIPLE_ACCOUNTS)}
                                    </span>
                                ) : (
                                    planCancelledInfo.suspendedAccounts.map((account, index) => (
                                        <span key={account.email}>
                                            {(planCancelledInfo.isOwnPlanCancelled || index > 0) && <br />}
                                            {t(translationKeys.banner.PLAN_CANCELLED_ACCOUNT, { email: account.email })}
                                        </span>
                                    ))
                                )}
                            </P>
                            <Icon
                                variant="close"
                                accessibilityDescription="Close"
                                height={16}
                                width={16}
                                onClick={() => {
                                    setPlanCancelledBannerDismissed(true);
                                    localStorage[LOCAL_STORAGE_PLAN_CANCELLED_BANNER_DISMISSED] = accountInfo?.uid;
                                }}
                            />
                        </PlanCancelledBanner>
                    );
                })()
            }

            <PageContent 
                $mobileBreakpoint={mobileBreakpoint} 
                $fullWidth={routeConfig?.layout?.fullWidth} 
                $background={backgroundGradient}
            >
                {/* Error boundaries placed around whole page and then page content (excluding tabs). */}
                <ErrorBoundary
                    errorComponent={ErrorScreen}
                    message={t(translationKeys.errors.PAGE_ERROR)}
                >
                    {
                        // If this route does not have its own component (i.e. a renderComponent function)
                        // and has subroutes, then render tabs
                        !routeConfig?.renderComponent &&
                            routeConfig?.routes &&
                            !isMobileApp && (
                                <Tabs
                                    large
                                    links={Object.values(routeConfig.routes)
                                        .map(({ translationKey, path }) => ({
                                            text: t(translationKey),
                                            path,
                                        }))}
                                />
                            )
                    }
                    <ErrorBoundary
                        errorComponent={ErrorScreen}
                        message={t(translationKeys.errors.PAGE_ERROR)}
                    >
                        {/* Context consumed by `useMobileAppHeader` on each page */}
                        <MobileAppHeaderContext.Provider
                            value={mobileAppHeaderContextValue}
                        >
                            {children}
                        </MobileAppHeaderContext.Provider>
                    </ErrorBoundary>
                </ErrorBoundary>
            </PageContent>
        </AppLayoutContainer>
    );
};

AppLayout.propTypes = {
    /** Config object from routes.js for the current route. Required unless on the 404 page. */
    routeConfig: PropTypes.object,
    /** Function called when user clicks 'Log out'. */
    logout: PropTypes.func.isRequired,
    /** Whether web app is being accessed through mobile app. Navigation is hidden if so. */
    mobileApp: PropTypes.bool,
    /** Account data of logged in user. */
    accountInfo: PropTypes.object.isRequired
};

const Sidebar = withTranslation()(
    ({
        logout,
        accountInfo,
        t,
    }) => {

        const { isDemo } = useDemo();
        const whiteLabelInfo = useWhiteLabelInfo();
        const { terms, privacy, termsPrivacyData } = whiteLabelInfo;
        const hideSmartMotionAlertsNav = isSitesecWhiteLabel(
            whiteLabelInfo,
            host
        );
        
        // State to force re-render when sessionStorage changes
        const [customIconFlag, setCustomIconFlag] = useState(() => 
            parseInt(sessionStorage.getItem('customIconFlag') || '0')
        );

        const [canAccessSmartMotionAlerts, setCanAccessSmartMotionAlerts] =
            useState(() => readCachedSmartMotionAccess(accountInfo?.uid) ?? false);

        // Work out (and cache) whether this user can see the Alerts nav link.
        // `getAllCameras()` is called from a lot of pages/components already, and
        // already known here and we don't need to fetch anything ourselves.
        // note this is still going to do a double call on load when pages make the call already just as before, would be good to dedupe at some point
        useEffect(() => {
            let cancelled = false;
            const uid = accountInfo?.uid;

            if (!uid) {
                setCanAccessSmartMotionAlerts(false);
                return undefined;
            }

            const cachedAlertsAccess = readCachedSmartMotionAccess(uid);

            if (cachedAlertsAccess !== null) {
                // Already known from a previous getAllCameras() call this session.
                // Trust it - the event listener below will pick up any future change.
                setCanAccessSmartMotionAlerts(cachedAlertsAccess);
                return undefined;
            }

            // Nothing has fetched the camera list yet this session (e.g. this is
            // the very first page loaded and it doesn't itself fetch cameras) -
            // fetch it once ourselves as a fallback. This also populates the
            // cache for everyone else via getAllCameras()'s own side effect.
            const loadAlertsAccess = async () => {
                try {
                    const cameras = await getAllCameras();
                    if (cancelled) {
                        return;
                    }
                    setCanAccessSmartMotionAlerts(computeSmartMotionAccess(cameras));
                } catch (error) {
                    if (!cancelled) {
                        setCanAccessSmartMotionAlerts(false);
                    }
                }
            };

            loadAlertsAccess();

            return () => {
                cancelled = true;
            };
        }, [accountInfo?.uid]);

        // Keep in sync whenever the cache is updated elsewhere - e.g. any other
        // page's getAllCameras() call recomputing this after a camera's smart
        // motion setting or camera access changes. Same-tab sessionStorage writes
        // don't fire the native 'storage' event, so a custom event is used instead.
        useEffect(() => {
            const uid = accountInfo?.uid;

            const handleSmartMotionAccessChanged = (event) => {
                if (uid && event.detail?.uid === uid) {
                    setCanAccessSmartMotionAlerts(event.detail.hasAccess);
                }
            };

            window.addEventListener(SMART_MOTION_ACCESS_CHANGED_EVENT, handleSmartMotionAccessChanged);

            return () => {
                window.removeEventListener(SMART_MOTION_ACCESS_CHANGED_EVENT, handleSmartMotionAccessChanged);
            };
        }, [accountInfo?.uid]);

        // Listen for sessionStorage changes
        useEffect(() => {
            const handleStorageChange = () => {
                setCustomIconFlag(parseInt(sessionStorage.getItem('customIconFlag') || '0'));
            };

            // Listen for storage events (from other tabs/windows)
            window.addEventListener('storage', handleStorageChange);
            
            // Check for changes periodically since storage events don't fire for same-origin changes
            const interval = setInterval(handleStorageChange, 1000);

            return () => {
                window.removeEventListener('storage', handleStorageChange);
                clearInterval(interval);
            };
        }, []);

        // Helper functions for external link actions
        const handleTermsClick = useCallback(() => {
            if (termsPrivacyData?.type === 'full-white-label') {
                const path = routes.fullWhiteLabelTerms?.path ?? '/terms-of-service';
                window.open(`${path}?app=true`, '_blank');
            } else if (terms) {
                window.open(`${terms}${terms.includes('?') ? '&' : '?'}app=true`, '_blank');
            } else {
                window.open('/terms.html?app=true', '_blank');
            }
        }, [terms, termsPrivacyData?.type]);

        const handlePrivacyClick = useCallback(() => {
            if (termsPrivacyData?.type === 'full-white-label') {
                const path = routes.fullWhiteLabelPrivacy?.path ?? '/privacy-policy';
                window.open(`${path}?app=true`, '_blank');
            } else if (privacy) {
                window.open(`${privacy}${privacy.includes('?') ? '&' : '?'}app=true`, '_blank');
            } else {
                window.open('/privacy.html?app=true', '_blank');
            }
        }, [privacy, termsPrivacyData?.type]);

        const [mainLinks, secondaryLinks, footerLinks] = useMemo(() => {
            const accountTooltipDescription = accountInfo?.email || accountInfo?.username;
            const [mainLinks, secondaryLinks] = _.reduce(
                routes,
                (acc, routeConfig) => {
                    if (
                        routeConfig.path === routes.alerts?.path &&
                        (hideSmartMotionAlertsNav ||
                            (!isDemo && !canAccessSmartMotionAlerts))
                    ) {
                        return acc;
                    }
                
                    // Exclude route from demo if needed
                    if (isDemo && !routeConfig.demo) {
                        return acc;
                    }

                    // Check whether to add route to sidebar
                    if (
                        (routeConfig.sidebarNav || routeConfig.sidebarNavSecondary) &&
                        canUserAccessRoute(routeConfig, accountInfo)
                    ) {
                        // Custom icon routes - check sessionStorage visibility
                        if (routeConfig.path === '/changeIcon' || routeConfig.path === '/resetIcon') {
                            
                            // Only show if customIconFlag matches the route
                            if ((routeConfig.path === '/changeIcon' && customIconFlag === 1) ||
                                (routeConfig.path === '/resetIcon' && customIconFlag === 2)) {
                                
                                const links = routeConfig.sidebarNav ? acc[0] : acc[1];
                                
                                if (isMobileApp) {
                                    // Use action button behavior on mobile
                                    links.push({
                                        text: routeConfig.path === '/changeIcon' ? 'Change Icon' : 'Reset Icon',
                                        action: () => {
                                            window.open(`${routeConfig.path}?app=true`, '_blank');
                                        },
                                        icon: routeConfig.icon,
                                        beta: routeConfig.beta
                                    });
                                } else {
                                    // Use normal navigation on desktop
                                    links.push({
                                        text: routeConfig.path === '/changeIcon' ? 'Change Icon' : 'Reset Icon',
                                        path: routeConfig.startPath ?? routeConfig.path,
                                        icon: routeConfig.icon,
                                        beta: routeConfig.beta
                                    });
                                }
                            }
                        } else {
                            // Regular route processing
                            const links = routeConfig.sidebarNav ? acc[0] : acc[1];
                            
                            // Special handling for "What's New" external route on mobile
                            if (routeConfig.path === '/product-updates' && isMobileApp) {
                                links.push({
                                    text: t(routeConfig.translationKey),
                                    action: () => {
                                        window.open('https://videoloft.com/product-updates?app=true', '_blank');
                                    },
                                    icon: routeConfig.icon,
                                    beta: routeConfig.beta
                                });
                            } else {
                                const tooltipDescription =
                                    routeConfig.path === routes.account.path
                                        ? accountTooltipDescription
                                        : routeConfig.tooltipDescriptionKey
                                            ? t(routeConfig.tooltipDescriptionKey)
                                            : undefined;
                                const tooltipMaxWidth = routeConfig.path === routes.account.path ? 'none' : undefined;
                                const tooltipNoWrap = routeConfig.path === routes.account.path;

                                links.push({
                                    text: t(routeConfig.translationKey),
                                    path: routeConfig.startPath ?? routeConfig.path,
                                    icon: routeConfig.icon,
                                    beta: routeConfig.beta,
                                    group: routeConfig.group,
                                    tooltipDescription,
                                    tooltipMaxWidth,
                                    tooltipNoWrap
                                });
                            }
                        }
                    }

                    return acc;
                },
                // mainLinks first, secondaryLinks second
                [[], []]
            );

            // Add 'Log out' at the end of secondary links
            secondaryLinks.push(
                {
                    // In demo, it should say "Exit" rather than "Log out"
                    text: t(isDemo ? translationKeys.navigation.EXIT : translationKeys.login.LOG_OUT),
                    action: logout,
                    icon: LogOutIcon,
                }
            );

            // Create footer links with same mobile logic as secondary links
            const footerLinks = [];
            
            // Terms link
            if (isMobileApp) {
                footerLinks.push({
                    text: t(translationKeys.footer.TERMS),
                    action: handleTermsClick,
                });
            } else {
                footerLinks.push({
                    text: t(translationKeys.footer.TERMS),
                    path: routes.terms.path,
                });
            }
            
            // Privacy link
            if (isMobileApp) {
                footerLinks.push({
                    text: t(translationKeys.footer.PRIVACY),
                    action: handlePrivacyClick,
                });
            } else {
                footerLinks.push({
                    text: t(translationKeys.footer.PRIVACY),
                    path: routes.privacy.path,
                });
            }

            return [
                mainLinks,
                secondaryLinks,
                footerLinks
            ];
        }, [accountInfo, logout, isDemo, t, handleTermsClick, handlePrivacyClick, customIconFlag, canAccessSmartMotionAlerts, hideSmartMotionAlertsNav]);

        return (
            <SidebarContainer>
                <WhiteLabel.Sidebar
                    mainLinks={mainLinks}
                    secondaryLinks={secondaryLinks}
                    footerLinks={footerLinks}
                />
            </SidebarContainer>
        );
    }
);

Sidebar.propTypes = {
    /** Function called when user clicks 'Log out'. */
    logout: PropTypes.func.isRequired,
    /** Account data of logged in user. */
    accountInfo: PropTypes.object.isRequired
};

export default withTranslation()(AppLayout);
